import React, { useMemo, useState } from 'react';

import styles from '../Analyst.module.css';

export type Column<T> = {
  key: string;
  name: string;
  value: (row: T) => string | number | null;
  render?: (row: T) => React.ReactNode;
  numeric?: boolean;
};

// Таблица с сортировкой по клику на заголовок и строкой итогов (если передана)
export function SortableTable<T>({ rows, columns, rowKey, total }: {
  rows: T[];
  columns: Array<Column<T>>;
  rowKey: (row: T, index: number) => string;
  total?: Record<string, React.ReactNode>;
}) {
  const [sort, setSort] = useState<{ key: string; desc: boolean } | null>(null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column) return rows;
    const cmp = (a: T, b: T) => {
      const va = column.value(a);
      const vb = column.value(b);
      if (column.numeric) return Number(va ?? 0) - Number(vb ?? 0);
      return String(va ?? '').localeCompare(String(vb ?? ''), 'ru');
    };
    return [...rows].sort((a, b) => (sort.desc ? -cmp(a, b) : cmp(a, b)));
  }, [rows, columns, sort]);

  if (!rows.length) return <div className={styles.empty}>Нет данных</div>;
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`${styles.sortable} ${c.numeric ? styles.num : ''}`}
                onClick={() => setSort(sort?.key === c.key ? { key: c.key, desc: !sort.desc } : { key: c.key, desc: true })}
              >
                {c.name}
                {sort?.key === c.key ? (sort.desc ? ' ↓' : ' ↑') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={rowKey(row, i)}>
              {columns.map((c) => (
                <td key={c.key} className={c.numeric ? styles.num : ''}>
                  {c.render ? c.render(row) : c.value(row)}
                </td>
              ))}
            </tr>
          ))}
          {total && (
            <tr className={styles.total}>
              {columns.map((c) => (
                <td key={c.key} className={c.numeric ? styles.num : ''}>
                  {total[c.key] ?? ''}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
